"use client";

import { useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import RevealOnScroll from "@/components/atoms/RevealOnScroll";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import type { RootState } from "@/store/store";

/**
 * Banner 11 — Middle Banner (ASU-010).
 *
 * Figma (Atlas Website, file PdDGTiH65FKjDxz4cIVllK):
 *   1750 12168:236 · 1430 12165:3 · 1230 12166:133 · 1080 12168:190 · mobile 12169:292
 *
 * A full-bleed background image (separate desktop / mobile uploads; decorative
 * shapes baked into the upload) holds a left square image (photo + overlay
 * text/logo baked in) beside a content column: heading (1 line desktop / 2
 * mobile), paragraph (3 lines desktop / 5 mobile), 1–3 tick bullets and two
 * CTAs — "Apply Now" (solid) + "Prospectus" (outline). Breakpoints map 1:1 to
 * the project theme: base = mobile, sm = 1080, md = 1230, lg = 1430, xl = 1750.
 */

const MAX_BULLETS = 3;

type Row = Record<string, any>;

const isOn = (v: unknown) => v === true || v === 1 || v === "1";

const activeSorted = (rows: Row[] | undefined) =>
  Array.isArray(rows)
    ? [...rows]
        .filter((r) => isOn(r?.status))
        .sort((a, b) => Number(a?.priority ?? 0) - Number(b?.priority ?? 0))
    : [];

/** Default bullet icon — Vuesax bold tick-circle (green circle, knockout check). */
const TickCircleIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 28 28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M14.0002 2.33337C7.57183 2.33337 2.3335 7.57171 2.3335 14C2.3335 20.4284 7.57183 25.6667 14.0002 25.6667C20.4285 25.6667 25.6668 20.4284 25.6668 14C25.6668 7.57171 20.4285 2.33337 14.0002 2.33337ZM19.5768 11.3167L12.9618 17.9317C12.7985 18.095 12.5768 18.1884 12.3435 18.1884C12.1102 18.1884 11.8885 18.095 11.7252 17.9317L8.4235 14.63C8.08516 14.2917 8.08516 13.7317 8.4235 13.3934C8.76183 13.055 9.32183 13.055 9.66016 13.3934L12.3435 16.0767L18.3402 10.08C18.6785 9.74171 19.2385 9.74171 19.5768 10.08C19.9152 10.4184 19.9152 10.9667 19.5768 11.3167Z"
      fill="currentColor"
    />
  </svg>
);

/** Right-pointing arrow — Vuesax linear arrow-up rotated 90° (line + chevron). */
const ArrowRightIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M12.0252 15.0583L17.0835 9.99998L12.0252 4.94165"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.91689 10H16.9419"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Banner11 = (props: any) => {
  const { data: sectionData } = props;
  const dispatch = useDispatch();
  const ref = useRef<HTMLElement>(null);

  // Brochure URL (same source as Banner2 / Banner10) for the lead-drop fallback
  // when a CTA has no explicit link.
  const brochureUrl = useSelector(
    (state: RootState) => state.globalslice?.course?.brochure || "",
  );

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) FireWidgetViewedEvent(ref, "middle_banner");
  // }, []);

  const {
    metadata = [], // bullet points (1–3)

    background_image = "",
    background_image_mobile = "",
    background_color = "#D6DE66",

    left_image = "",

    title_1 = "",
    title_1_color = "#081F3D",
    paragraph_text = "",
    paragraph_color = "#081F3D",
    bullet_icon = "",

    // Primary CTA ("Apply Now" — solid) → opens the lead form (REGISTER)
    download_brochure_text = "Apply Now",
    button_color = "#081F3D",
    cta_text_color = "#FFFFFF",

    // Secondary CTA ("Prospectus" — outline) → brochure-download lead drawer
    apply_now_text = "Prospectus",
    apply_now_color = "#081F3D",
    apply_now_border_color = "#081F3D",

    enable_animation,
    status = true,
  } = sectionData || {};

  const isAnimationEnabled = isOn(enable_animation);

  const bullets = useMemo(
    () => activeSorted(metadata).slice(0, MAX_BULLETS),
    [metadata],
  );

  const primaryLabel = String(download_brochure_text || "").trim();
  const secondaryLabel = String(apply_now_text || "").trim();

  if (status === false || status === 0 || status === "0") return null;

  // Primary "Apply Now" → opens the lead form (REGISTER), same as Banner 3 / nav.
  const handleApplyNow = () => {
    ctaClick({
      section: "middle_banner",
      component: "apply_now",
      label: primaryLabel,
      step: "1",
      entity: "middle_banner",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  };

  // Secondary "Prospectus" → brochure-download lead drawer, same as Skills 1.
  const handleProspectus = () => {
    ctaClick({
      section: "middle_banner",
      component: "download_brochure",
      label: secondaryLabel,
      step: "1",
      entity: "middle_banner",
    });
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
        downloadLink: brochureUrl,
      }),
    );
  };

  /** Solid (primary) / outline (secondary) CTA — identical metrics, different skin. */
  const ctaClassName = cn(
    "flex w-full items-center justify-center gap-[8px] rounded-[10px] sm:w-auto",
    "px-[22px] py-[14px] lg:px-[24px] lg:py-[16px]",
    "font-poppins font-semibold leading-[1.4] tracking-[0.02em]",
    "text-[14px] sm:text-[16px] lg:text-[18px] xl:text-[20px]",
    "cursor-pointer transition-opacity hover:opacity-90",
  );

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: background_color }}
    >
      {/* Full-bleed widget background (decorative shapes baked into the upload).
          Each upload is scoped to its own breakpoint with no cross-fallback:
          the desktop image shows only at sm+ (≥1080px), the mobile image only
          below sm. A breakpoint with no upload falls back to `background_color`.
          `loading="lazy"` keeps the hidden variant from being fetched on the
          other viewport. sm = 1080px (see @theme). */}
      {background_image && (
        <img
          src={background_image}
          alt=""
          aria-hidden
          loading="lazy"
          className="pointer-events-none absolute inset-0 hidden size-full object-cover sm:block"
        />
      )}
      {background_image_mobile && (
        <img
          src={background_image_mobile}
          alt=""
          aria-hidden
          loading="lazy"
          className="pointer-events-none absolute inset-0 block size-full object-cover sm:hidden"
        />
      )}

      <div
        className={cn(
          "relative z-10 flex w-full flex-col items-start gap-[40px] px-[24px] py-[40px]",
          "sm:flex-row sm:items-center sm:gap-[40px] sm:px-[40px] sm:py-[28px]",
          "md:gap-[50px] md:px-[60px] md:py-[32px]",
          "lg:gap-[60px] lg:px-[80px] lg:py-[40px]",
          "xl:gap-[80px] xl:px-[160px] xl:py-[60px]",
        )}
      >
        {/* ── Left: square image (photo + overlay baked into the upload) ──── */}
        {left_image && (
          <div
            className={cn(
              "relative aspect-square w-full shrink-0 overflow-hidden rounded-[20px]",
              "sm:aspect-auto sm:size-[300px] md:size-[320px] lg:size-[340px] xl:size-[380px]",
            )}
          >
            <img
              src={left_image}
              alt=""
              loading="lazy"
              className="pointer-events-none absolute inset-0 size-full object-cover"
            />
          </div>
        )}

        {/* ── Right: content column ──────────────────────────────────────── */}
        <div
          className={cn(
            "flex w-full min-w-0 flex-col items-start gap-[24px]",
            "sm:flex-1 sm:gap-[28px] lg:gap-[32px] xl:gap-[40px]",
          )}
        >
          {/* Heading + paragraph */}
          {(title_1 || paragraph_text) && (
            <RevealOnScroll
              className="w-full"
              y={20}
              enableAnimation={isAnimationEnabled}
            >
              <div className="flex w-full flex-col gap-[14px] sm:gap-[18px]">
                {title_1 && (
                  <h2
                    className={cn(
                      "w-full font-poppins font-bold leading-[1.2] tracking-[-0.02em] lg:leading-[1.15]",
                      "line-clamp-2 sm:line-clamp-1",
                      "text-[28px] sm:text-[36px] md:text-[40px] lg:text-[48px] xl:text-[56px]",
                    )}
                    style={{ color: title_1_color }}
                  >
                    {title_1}
                  </h2>
                )}
                {paragraph_text && (
                  <p
                    className={cn(
                      "w-full font-poppins font-normal leading-[1.6] xl:leading-[1.55]",
                      "line-clamp-5 sm:line-clamp-3",
                      "text-[14px] sm:text-[18px] lg:text-[20px] xl:text-[22px]",
                    )}
                    style={{ color: paragraph_color }}
                  >
                    {paragraph_text}
                  </p>
                )}
              </div>
            </RevealOnScroll>
          )}

          {/* Bullets (1–3) — stacked on mobile, inline row on desktop */}
          {bullets.length > 0 && (
            <RevealOnScroll
              className="w-full"
              y={20}
              delay={80}
              enableAnimation={isAnimationEnabled}
            >
              <div
                className={cn(
                  "flex w-full flex-col gap-[8px]",
                  "sm:flex-row sm:flex-wrap sm:items-start sm:gap-x-[28px] sm:gap-y-[4px]",
                  "md:gap-x-[32px] lg:gap-x-[40px]",
                )}
              >
                {bullets.map((bl, i) => (
                  <div
                    key={bl.id ?? i}
                    className={cn(
                      "flex w-full items-center gap-[10px] py-[4px]",
                      "sm:w-auto sm:py-[8px] lg:py-[10px]",
                    )}
                  >
                    {bl.icon || bullet_icon ? (
                      <img
                        src={bl.icon || bullet_icon}
                        alt=""
                        className="size-[20px] shrink-0 object-contain sm:size-[24px] lg:size-[28px]"
                      />
                    ) : (
                      <TickCircleIcon className="size-[20px] shrink-0 text-[#357E51] sm:size-[24px] lg:size-[28px]" />
                    )}
                    <span
                      className={cn(
                        "font-poppins font-semibold leading-[1.5] xl:leading-[1.4]",
                        "text-[14px] sm:whitespace-nowrap sm:text-[16px] lg:text-[18px] xl:text-[20px]",
                      )}
                      style={{ color: bl.text_color || paragraph_color }}
                    >
                      {bl.text}
                    </span>
                  </div>
                ))}
              </div>
            </RevealOnScroll>
          )}

          {/* CTAs — stacked full-width on mobile, inline row on desktop */}
          {(primaryLabel || secondaryLabel) && (
            <RevealOnScroll
              className="w-full sm:w-auto"
              y={20}
              delay={160}
              enableAnimation={isAnimationEnabled}
            >
              <div
                className={cn(
                  "flex w-full flex-col gap-[20px]",
                  "sm:w-auto sm:flex-row sm:items-center md:gap-[24px]",
                )}
              >
                {primaryLabel && (
                  <button
                    type="button"
                    onClick={handleApplyNow}
                    className={ctaClassName}
                    style={{ backgroundColor: button_color, color: cta_text_color }}
                  >
                    <span className="whitespace-nowrap">{primaryLabel}</span>
                    <ArrowRightIcon className="size-[20px] shrink-0" />
                  </button>
                )}
                {secondaryLabel && (
                  <button
                    type="button"
                    onClick={handleProspectus}
                    className={cn(ctaClassName, "border border-solid bg-transparent")}
                    style={{ borderColor: apply_now_border_color, color: apply_now_color }}
                  >
                    <span className="whitespace-nowrap">{secondaryLabel}</span>
                    <ArrowRightIcon className="size-[20px] shrink-0" />
                  </button>
                )}
              </div>
            </RevealOnScroll>
          )}
        </div>
      </div>
    </section>
  );
};

export default Banner11;
